import type { IntlShape } from 'react-intl';

import type { TriggerType } from '@atlaskit/editor-common/analytics';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { Command, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { keydownHandler } from '@atlaskit/editor-prosemirror/keymap';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AiAutocompletePlugin } from '../../aiAutocompletePluginType';
import { aiAutocompletePluginKey } from '../../ui/components/plugin-key';
import { ACTIONS } from '../../ui/types/types';
import { isCursorInAllowedNode } from '../../ui/utils/allowedNodeCheck';
import {
	addAutocompleteInvokedAnalytics,
	addAutocompleteViewedAnalytics,
} from '../../ui/utils/analytics';
import { exceedsMinDocumentWords, exceedsNodeCharLimit } from '../../ui/utils/charAndWordCount';
import {
	get1BasedLineIndexFromDocStart,
	isCursorWithinDocEnd,
	isCursorWithinDocStart,
} from '../../ui/utils/docRegionCheck';
import { getHeadingContext } from '../../ui/utils/headingUtils';
import { matchesKeywordTrigger } from '../../ui/utils/keywordDetection';
import {
	getConclusionKeywords,
	getNextStepsKeywords,
	getSummaryKeywords,
} from '../../ui/utils/triggerKeywords';
/**
 * True for keys that typically mutate document text (printable chars,
 * Backspace, Delete, Enter) when not combined with meta/ctrl modifiers.
 */
function isDocMutatingKey(event: KeyboardEvent): boolean {
	if (event.isComposing || event.key === 'Process') {
		return false;
	}
	if (event.key === 'Backspace' || event.key === 'Delete') {
		return true;
	}
	if (event.key === 'Enter' && !event.metaKey && !event.ctrlKey) {
		return true;
	}
	return event.key.length === 1 && !event.metaKey && !event.ctrlKey;
}

type PluginParams = {
	api: ExtractInjectionAPI<AiAutocompletePlugin> | undefined;
	getIntl: () => IntlShape;
};

type SectionTriggerConfig = {
	headingTriggerType: TriggerType;
	keywords: readonly string[];
	nonHeadingTriggerType: TriggerType;
	sectionType: string;
};

/**
 * Keymap plugin for AI heading autocomplete: manual trigger, accept, dismiss,
 * Enter-reveal flow for auto triggers, and same-line editing tolerance.
 */
export function createAiAutocompleteKeymapPlugin({ api, getIntl }: PluginParams): SafePlugin {
	const editorAnalyticsAPI = api?.analytics?.actions;

	const createSectionKeywordTrigger = (config: SectionTriggerConfig) => {
		let pendingIdleId: number | undefined;

		const cancel = (): void => {
			if (pendingIdleId === undefined) {
				return;
			}
			if (typeof cancelIdleCallback === 'function') {
				cancelIdleCallback(pendingIdleId);
			} else {
				cancelAnimationFrame(pendingIdleId);
			}
			pendingIdleId = undefined;
		};

		/**
		 * Pattern matching runs in an idle callback (see block-controls / insert-block)
		 * so we do not add work on the critical key path; DOM + doc are up to date by then.
		 */
		const schedule = (view: EditorView): void => {
			cancel();

			const flush = () => {
				pendingIdleId = undefined;
				if (view.isDestroyed) {
					return;
				}

				if (
					matchesKeywordTrigger(view.state, config.keywords) &&
					!exceedsNodeCharLimit(view.state)
				) {
					const pluginState = aiAutocompletePluginKey.getState(view.state);
					if (pluginState && pluginState.status !== 'idle') {
						api?.core?.actions?.execute?.(api?.aiAutocomplete?.commands?.dismissSuggestion?.());
					}
					const { from: resolvedCursorPos } = view.state.selection;
					const invokeTr = view.state.tr.setMeta(aiAutocompletePluginKey, {
						type: ACTIONS.TRIGGER_AUTO,
						cursorPos: resolvedCursorPos,
						triggerType: config.sectionType,
					});

					const triggerType = getHeadingContext(view.state).cursorInHeading
						? config.headingTriggerType
						: config.nonHeadingTriggerType;

					addAutocompleteInvokedAnalytics(editorAnalyticsAPI)(invokeTr, triggerType);

					view.dispatch(invokeTr);
				}
			};

			if (typeof requestIdleCallback === 'function') {
				pendingIdleId = requestIdleCallback(flush, { timeout: 2000 });
			} else {
				pendingIdleId = requestAnimationFrame(flush);
			}
		};

		return { cancel, schedule };
	};

	const summaryTrigger = createSectionKeywordTrigger({
		keywords: getSummaryKeywords(getIntl),
		sectionType: 'summary',
		headingTriggerType: 'summary-heading',
		nonHeadingTriggerType: 'summary-first-lines',
	});

	const conclusionTrigger = createSectionKeywordTrigger({
		keywords: getConclusionKeywords(getIntl),
		sectionType: 'conclusion',
		headingTriggerType: 'conclusion-heading',
		nonHeadingTriggerType: 'conclusion-last-lines',
	});

	const nextStepsTrigger = createSectionKeywordTrigger({
		keywords: getNextStepsKeywords(getIntl),
		sectionType: 'next-steps',
		headingTriggerType: 'next-steps-heading',
		nonHeadingTriggerType: 'next-steps-last-lines',
	});

	const triggerManualInline: Command = (state, dispatch, view) => {
		if (!dispatch || !view) {
			return false;
		}

		const pluginState = aiAutocompletePluginKey.getState(state);
		if (pluginState && pluginState.status !== 'idle') {
			api?.core?.actions?.execute?.(api?.aiAutocomplete?.commands?.dismissSuggestion?.());
		}

		const freshState = view.state;
		const resolvedCursorPos = freshState.selection.from;
		const invokeTr = freshState.tr.setMeta(aiAutocompletePluginKey, {
			type: ACTIONS.TRIGGER_MANUAL,
			cursorPos: resolvedCursorPos,
		});
		addAutocompleteInvokedAnalytics(editorAnalyticsAPI)(invokeTr, 'cmd+shift+space');
		view.dispatch(invokeTr);
		return true;
	};

	// ── Enter: auto-trigger Enter-reveal flow ─────────────────────────
	// Returns `false` in every branch so ProseMirror still processes
	// the Enter key (new paragraph / split node).  Side effects (flag
	// set, dismiss, scheduled reveal) happen before that.
	const handleEnter: Command = (state, _dispatch, view) => {
		const pluginState = aiAutocompletePluginKey.getState(state);

		// Active state + exceeds char limit → dismiss, let Enter through
		if (pluginState && pluginState.status !== 'idle' && exceedsNodeCharLimit(state)) {
			api?.core?.actions?.execute?.(api?.aiAutocomplete?.commands?.dismissSuggestion?.());
			return false;
		}

		// Loading + auto: flag that Enter was pressed so the API
		// response handler can show the suggestion immediately
		if (pluginState?.status === 'loading' && pluginState.trigger === 'auto' && view) {
			view.dispatch(
				view.state.tr.setMeta(aiAutocompletePluginKey, { type: ACTIONS.ENTER_DURING_LOADING }),
			);
			return false;
		}

		// suggestion_pending: reveal ghost at new cursor after Enter creates a new block
		if (pluginState?.status === 'suggestion_pending' && view) {
			setTimeout(() => {
				if (view.isDestroyed) {
					return;
				}
				const pending = aiAutocompletePluginKey.getState(view.state);
				if (pending?.status !== 'suggestion_pending') {
					return;
				}
				const cursorPos = view.state.selection.from;
				const revealTr = view.state.tr.setMeta(aiAutocompletePluginKey, {
					type: ACTIONS.REVEAL_SUGGESTION,
					cursorPos,
				});
				addAutocompleteViewedAnalytics(editorAnalyticsAPI)(revealTr);
				view.dispatch(revealTr);
			}, 0);
			return false;
		}

		return false;
	};

	const bindings: Record<string, Command> = {
		['Mod-Shift-Space']: triggerManualInline,
		['Meta-Shift-Space']: triggerManualInline,
		['Enter']: handleEnter,
		['Tab']: (state, dispatch) => {
			const pluginState = aiAutocompletePluginKey.getState(state);
			if (pluginState?.status !== 'suggestion') {
				return false;
			}
			if (!dispatch) {
				return false;
			}
			return (
				api?.core?.actions?.execute?.(
					api?.aiAutocomplete?.commands?.acceptSuggestion?.({
						cursorPos: pluginState.cursorPos,
						suggestion: pluginState.suggestion,
					}),
				) ?? false
			);
		},
		['Escape']: (state, dispatch) => {
			const pluginState = aiAutocompletePluginKey.getState(state);
			if (!pluginState || pluginState.status === 'idle') {
				return false;
			}
			if (!dispatch) {
				return false;
			}
			return (
				api?.core?.actions?.execute?.(api?.aiAutocomplete?.commands?.dismissSuggestion?.()) ?? false
			);
		},
	};

	return new SafePlugin({
		view() {
			return {
				destroy() {
					summaryTrigger.cancel();
					conclusionTrigger.cancel();
					nextStepsTrigger.cancel();
				},
			};
		},
		props: {
			handleKeyDown(view, event) {
				if (keydownHandler(bindings)(view, event)) {
					return true;
				}

				// Re-read state: the Enter binding may have dispatched a
				// dismiss (char-limit case), making the state idle already.
				const pluginState = aiAutocompletePluginKey.getState(view.state);

				// The Enter binding returns false (to let the default Enter
				// through) but already set flags / scheduled reveal for these
				// states — don't let the generic dismiss undo that work.
				if (
					event.key === 'Enter' &&
					!event.shiftKey &&
					pluginState &&
					(pluginState.status === 'loading' || pluginState.status === 'suggestion_pending')
				) {
					return false;
				}

				// ── Auto loading / pending: same-line editing tolerance ────────
				// Allow document-mutating keys on the trigger line without
				// dismissing the session; once the cursor moves below the trigger
				// line, further typing dismisses.
				if (
					pluginState &&
					(pluginState.status === 'loading' || pluginState.status === 'suggestion_pending') &&
					pluginState.trigger === 'auto' &&
					isDocMutatingKey(event)
				) {
					const lineNow = get1BasedLineIndexFromDocStart(view.state.doc, view.state.selection.from);
					if (lineNow <= pluginState.triggerLineIndex) {
						return false;
					}
				}

				// ── Generic: dismiss any active state ─────────────────────────
				if (pluginState && pluginState.status !== 'idle') {
					api?.core?.actions?.execute?.(api?.aiAutocomplete?.commands?.dismissSuggestion?.());
				}

				// ── Schedule keyword checks ───────────────────────────────────
				if (isCursorInAllowedNode(view.state) && exceedsMinDocumentWords(view.state)) {
					if (isCursorWithinDocStart(view.state)) {
						summaryTrigger.schedule(view);
					}
					if (isCursorWithinDocEnd(view.state)) {
						conclusionTrigger.schedule(view);
						nextStepsTrigger.schedule(view);
					}
				}

				return false;
			},
		},
	});
}
